import clsx from 'clsx'

import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { ResultQuickAdd } from 'libs/searchspring/components/ResultQuickAdd'

const DEFAULT_OPTIONS = {
  useMiniCard: false,
  hideQuickAdd: true,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0' }) {
  const [variant] = result.variants
  const isB2BCustomer = window.globals?.customer?.b2b

  // For non-B2B customers, use regular pricing
  const finalPrice = variant.price
  const finalCompareAtPrice = variant.compare_at_price

  const {
    useMiniCard = DEFAULT_OPTIONS.useMiniCard,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist
  } = options
  const { currentResult } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const criteria = useMemo(
    () => ({
      tags: currentResult?.tags ?? [],
      collections: currentResult?.collection_handle ?? [],
      products: [currentResult.handle]
    }),
    [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle]
  )

  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find((item) => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div
      className={clsx('group/card relative flex grow', className)}
      id={`result__${currentResult.handle}`}
      data-dl-track="select_item"
      data-dl-product-id={currentResult.uid}
      data-dl-index={dataIndex}
    >
      {useMiniCard ? (
        <div className="flex w-full grow items-stretch gap-x-1 relative border border-neutral-medium p-1">
          <div className="overflow-hidden relative aspect-3/4 w-[90px] shrink-0">
            <a
              href={url}
              onClick={handleLogClick}
              className="size-full children:size-full children:object-cover"
              aria-label={currentResult.name}
            >
              <ResultImage result={currentResult} />
            </a>
          </div>
          <div className="flex grow flex-col justify-between gap-y-1">
            <div className="flex justify-between gap-x-0.625 lg-max:flex-col">
              <a
                href={url}
                onClick={handleLogClick}
                className="paragraph-s font-medium link-fade"
                aria-label={currentResult.name}
              >
                <span dangerouslySetInnerHTML={{ __html: currentResult.name }} />
              </a>
              {isB2BCustomer ? (
                <b2b-price className="group/price paragraph-s flex items-center" data-variant-id={variant?.id}>
                  <span data-b2b-price className="paragraph-s group-data-[sale=true]/price:text-brand-sale"></span>
                  <span
                    data-original-price
                    className="paragraph-s ml-0.5 hidden text-neutral-secondary line-through group-data-[sale=true]/price:block"
                  ></span>
                </b2b-price>
              ) : (
                <Price
                  price={finalPrice}
                  compareAtPrice={finalCompareAtPrice}
                  className="group/price paragraph-s flex lg:flex-col lg:justify-end lg:text-right lg-max:items-center"
                />
              )}
            </div>
            <ResultQuickAdd result={currentResult} />
          </div>
        </div>
      ) : (
        <div className="flex w-full grow flex-col gap-y-1">
          <div className="overflow-hidden relative aspect-3/4 w-full">
            <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
              <ResultImage result={currentResult}></ResultImage>
            </a>
            {!hideBadges && (firstBadge || secondBadge) && (
              <div className="absolute left-0.5 top-0.5 z-2 flex max-w-[80%] flex-col gap-y-0.125 lg:left-0.75 lg:top-0.75 xl:left-1 xl:top-1">
                {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
              </div>
            )}
            {!hidePromolines && firstPromoline && (
              <div className="absolute z-2 flex w-full flex-col inset-bl lg:gap-0.25 xl:p-1">
                {firstPromoline && (
                  <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />
                )}
              </div>
            )}
            {/* WishlistKing Button */}
            {!hideWishlist && (
              <div className="wishlist-button absolute right-0.5 top-0.5 z-2 flex items-center justify-center lg:right-0.75 xl:right-1 xl:top-0.75">
                <button
                  type="button"
                  data-product-handle={currentResult.handle}
                  aria-label="Add to Wishlist"
                  className="wkh-button save-wishlist flex aspect-1/1 w-[36px] items-center justify-center rounded-8 transition-all hover:lg:!bg-neutral-white group-hover/card:lg:bg-neutral-white/80"
                >
                  <div className="size-[17px] children:size-full">
                    <svg
                      className="wkh-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="19"
                      viewBox="0 0 20 19"
                      fill="none"
                    >
                      <path
                        d="M16.3407 10.8381C17.7072 9.49914 19.092 7.89421 19.092 5.79405C19.092 4.45629 18.5606 3.17331 17.6146 2.22737C16.6687 1.28143 15.3857 0.75 14.048 0.75C12.4339 0.75 11.2967 1.20855 9.92101 2.5842C8.54536 1.20855 7.40815 0.75 5.79405 0.75C4.45629 0.75 3.17331 1.28143 2.22737 2.22737C1.28143 3.17331 0.75 4.45629 0.75 5.79405C0.75 7.90339 2.12565 9.50831 3.5013 10.8381L9.92101 17.2578L16.3407 10.8381Z"
                        fill="var(--icon-fill)"
                        stroke="context-stroke"
                        stroke-width="context-stroke-width"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            )}
          </div>
          <div className="flex grow flex-col gap-y-0.5 px-1 lg:gap-y-0.125">
            <div className="flex justify-between gap-x-1 gap-y-0.25 xxl-max:flex-col image-view-large:flex-row">
              <a
                href={url}
                className="paragraph-s font-medium link-fade"
                aria-label={currentResult.name}
                dangerouslySetInnerHTML={{ __html: currentResult.name }}
              />
              {isB2BCustomer ? (
                <b2b-price className="group/price flex gap-x-0.25" data-variant-id={result.variants[0].id}>
                  <span
                    data-original-price
                    className="paragraph-s hidden line-through group-data-[sale=true]/price:block"
                  ></span>
                  <span data-b2b-price className="paragraph-s group-data-[sale=true]/price:text-brand-red"></span>
                </b2b-price>
              ) : (
                <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="flex gap-x-0.25" />
              )}
            </div>
            <div className="flex justify-between gap-x-1 gap-y-0.25 xxl-max:flex-col image-view-large:flex-row">
              <div className="grow">
                {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} />}
              </div>
              <klaviyo-star-rating
                product-id={currentResult.uid}
                product-title={currentResult.name}
                product-type={currentResult.type}
              ></klaviyo-star-rating>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
